package com.swetdot.swetgram.data.profile

import com.swetdot.swetgram.data.api.ApiClient
import com.swetdot.swetgram.data.toDomain
import com.swetdot.swetgram.domain.AccountRole
import com.swetdot.swetgram.domain.FieldEdit
import com.swetdot.swetgram.domain.MyProfile
import com.swetdot.swetgram.domain.ProfileEdit
import com.swetdot.swetgram.domain.ProfileQr
import com.swetdot.swetgram.domain.ProfileVisibility
import com.swetdot.swetgram.domain.QrTarget
import com.swetdot.swetgram.domain.ScheduledDeletion
import com.swetdot.swetgram.domain.UserId
import com.swetdot.swetgram.domain.UserProfile
import com.swetdot.swetgram.protocol.dto.AccountDeletionStatus
import com.swetdot.swetgram.protocol.dto.ChangePasswordRequest
import com.swetdot.swetgram.protocol.dto.ConfirmEmailChangeRequest
import com.swetdot.swetgram.protocol.dto.QrCode
import com.swetdot.swetgram.protocol.dto.RequestEmailChange
import io.ktor.client.request.setBody
import io.ktor.http.ContentType
import io.ktor.http.HttpMethod
import io.ktor.http.content.TextContent
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.serialization.json.JsonNull
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonObjectBuilder
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.put
import kotlin.time.Instant
import com.swetdot.swetgram.protocol.dto.ProfileVisibility as ProfileVisibilityDto
import com.swetdot.swetgram.protocol.dto.QrTargetType as QrTargetTypeDto
import com.swetdot.swetgram.protocol.dto.UserPrivate as UserPrivateDto
import com.swetdot.swetgram.protocol.dto.UserPublic as UserPublicDto
import com.swetdot.swetgram.protocol.dto.UserRole as UserRoleDto

/**
 * The account surface: the signed-in user's own profile, other people's profiles, credentials,
 * QR codes and scheduled deletion.
 *
 * There is no PERSISTENT cache behind any of it, deliberately. The chat list and the timeline are
 * cache-first because they must render offline and instantly; a profile edit is the opposite --
 * it is a form the user is looking at, and showing a stale `version` from SQLite would produce a
 * `409 version_conflict` on save with nothing on screen to explain it. Nothing here is ever read
 * INSTEAD of a request. [current] is the separate question of which in-memory copy the screens
 * render, and it is documented on the property.
 *
 * [AuthRepository][com.swetdot.swetgram.data.auth.AuthRepository] also reads `/users/me`. That is
 * not a duplicate: it answers "who is signed in" and returns the shared [User] type, which has no
 * email, no verification flag and no version. Everything this file writes depends on the version,
 * so the profile screen needs the fuller [MyProfile].
 */
class ProfileRepository(
    private val api: ApiClient,
) {
    private val _current = MutableStateFlow<MyProfile?>(null)

    /**
     * THE SIGNED-IN ACCOUNT, AS LAST CONFIRMED BY THE SERVER -- one copy, shared by every screen.
     *
     * It exists because the Profile tab showed the OLD display name, handle, bio and avatar after
     * an edit, for as long as the process lived. Three view models read this one mutable resource
     * and each kept a private copy: `MyProfileViewModel` (scoped to the tab's navigation entry, so
     * it outlives every screen pushed over it), `EditProfileViewModel` and `SettingsViewModel`. The
     * edit screen wrote the saved profile into ITS copy, popped, and the tab underneath went on
     * rendering the copy it had read once. Nothing was stale on the wire and nothing failed; the
     * two copies simply never met.
     *
     * So the repository owns the value and the view models render it. [me] and [updateProfile] are
     * the only two operations that learn a fresh `UserPrivate`, and both publish here.
     *
     * THIS IS NOT THE CACHE THIS PACKAGE DELIBERATELY HAS NONE OF. That rule is about serving a
     * SETTINGS FORM from a stored answer -- see the class comment and `PrivacyRepository` -- and
     * nothing here is read instead of a request: every screen still calls [me] on arrival. What
     * changes is only that the one already-in-memory copy is shared rather than duplicated, which
     * strictly reduces how stale any screen can be.
     *
     * Null until the first read, and null again after [forget].
     */
    val current: StateFlow<MyProfile?> = _current.asStateFlow()

    /** `getMe`. */
    suspend fun me(): MyProfile = api.get<UserPrivateDto>("users/me").toDomain().also { _current.value = it }

    /**
     * Drops the published profile.
     *
     * MUST be called wherever the session ends, and for the same reason the SQLite cache is
     * cleared there: this object outlives the navigation graph, so without it the next account to
     * sign in renders the previous one's name, handle and photo until `GET /users/me` answers.
     * `AppContainer` wires both endings -- the button, and a session the server revoked.
     */
    fun forget() {
        _current.value = null
    }

    /**
     * `updateMe`. Sends ONLY the fields the edit mentions, so a concurrent change to a field this
     * form never touched is not overwritten by a read-modify-write.
     *
     * The body is hand-built as JSON rather than encoded from a DTO because the contract needs
     * three states per nullable field -- absent, explicit null, value -- and `ProtocolJson` sets
     * `explicitNulls = false`, which erases the middle one. A cleared bio must arrive as
     * `"bio": null`; if it arrived as an absent key the server would leave the old text in place
     * and answer 200, and the user would watch their bio come back.
     *
     * Throws [IllegalArgumentException] before any request when the edit is empty: the contract's
     * `minProperties: 2` makes that a guaranteed 400.
     */
    suspend fun updateProfile(edit: ProfileEdit): MyProfile {
        require(edit.hasChanges) { "A profile edit must change at least one field" }
        // `JsonElement.toString()` is the tree's own rendering, so no Json configuration -- and
        // therefore no `explicitNulls` -- sits between the null and the wire. TextContent is an
        // OutgoingContent, which content negotiation passes through untouched for the same
        // reason: nothing may get a second opinion about this body.
        val body = TextContent(edit.toJsonBody().toString(), ContentType.Application.Json)
        // Published, so the Profile tab under the edit screen renders the name that was just
        // saved rather than the one it read when the app started -- see [current]. The response
        // carries the new `version` too, which is what the next edit has to be pinned to.
        return api.patch<UserPrivateDto>("users/me") { setBody(body) }.toDomain()
            .also { _current.value = it }
    }

    /**
     * `getUserByUsername`. Normalised to lowercase because the contract's `Username` is
     * lowercase-only -- a mixed-case path would be rejected rather than resolved.
     */
    suspend fun userByUsername(username: String): UserProfile =
        api.get<UserPublicDto>("users/${username.trim().lowercase()}").toUserProfile()

    /**
     * `changePassword`. 204.
     *
     * The contract makes this a PUT, and [ApiClient] has no `put` helper, so it goes through
     * [ApiClient.execute] directly rather than growing the shared client a method for one caller.
     *
     * The server revokes every OTHER session and keeps this one, which is what someone who
     * distrusts a device is asking for, so there is nothing for the client to clean up locally.
     */
    suspend fun changePassword(currentPassword: String, newPassword: String) {
        api.execute<Unit>(HttpMethod.Put, "users/me/password", idempotencyKey = null) {
            setBody(ChangePasswordRequest(currentPassword = currentPassword, newPassword = newPassword))
        }
    }

    /**
     * `requestEmailChange`. 202, no body -- the confirmation goes to the NEW address, so nothing
     * comes back that could echo the pending address to a stolen session.
     *
     * [password] re-authenticates. Never store it, never put it in a retry buffer.
     */
    suspend fun requestEmailChange(newEmail: String, password: String) {
        api.post<Unit>("users/me/email/change-request") {
            setBody(RequestEmailChange(newEmail = newEmail.trim(), password = password))
        }
    }

    /**
     * `confirmEmailChange`. 204.
     *
     * The account's email and its `version` both change here, so any [ProfileEdit] the user
     * already has open is stale and would 409 -- callers must re-read [me] afterwards.
     */
    suspend fun confirmEmailChange(token: String) {
        api.post<Unit>("users/me/email/confirm") {
            setBody(ConfirmEmailChangeRequest(token = token.trim()))
        }
    }

    /**
     * `getMyProfileQr`.
     *
     * Keeps serving regardless of `allowProfileLink`. DECISIONS §4 makes a disabled link
     * AUTHENTICATED rather than nonexistent, and this endpoint is authenticated -- and the screen
     * that offers to turn the link back on is the one showing this code, so refusing here would
     * break the way out of the state.
     */
    suspend fun myQr(): ProfileQr = api.get<QrCode>("users/me/qr").toDomain()

    /** `getUserProfileQr`. Keyed by the immutable user id, never by username. */
    suspend fun qrFor(userId: UserId): ProfileQr = api.get<QrCode>("users/${userId.value}/qr").toDomain()

    /** `requestAccountDeletion`. 202; the account survives for the recovery window. */
    suspend fun scheduleDeletion(): ScheduledDeletion =
        api.post<AccountDeletionStatus>("users/me/deletion").toDomain()

    /** `cancelAccountDeletion`. 204. Valid until the moment the deletion runs. */
    suspend fun cancelDeletion() {
        api.delete<Unit>("users/me/deletion")
    }
}

// ------------------------------------------------------------------------------- mapping

/*
 * The wire-to-domain boundary for this package (IMPLEMENTATION_NOTES §10). `data/Mappers.kt`
 * owns the types the chat screens share; these are the profile-only ones, kept here so the
 * three repositories in this package can reuse them without widening anything else.
 */

/** Forgiving on purpose: an unreadable date degrades one field rather than failing a screen. */
internal fun parseTimestamp(raw: String?): Instant? =
    raw?.let { runCatching { Instant.parse(it) }.getOrNull() }

/**
 * `UserPrivate` repeats `UserPublic`'s fields flat -- the contract composes them with `allOf` --
 * so the shared mapping runs through `UserPublic` rather than being written out twice.
 */
internal fun UserPrivateDto.toDomain(): MyProfile = MyProfile(
    user = UserPublicDto(
        id = id,
        username = username,
        displayName = displayName,
        avatarUrl = avatarUrl,
        bio = bio,
        dateOfBirth = dateOfBirth,
        showBirthYear = showBirthYear,
        location = location,
        isOnline = isOnline,
        lastSeenAt = lastSeenAt,
        createdAt = createdAt,
        profileUrl = profileUrl,
        isBlockedByMe = isBlockedByMe,
        profileVisibility = profileVisibility,
        hasBlackBadge = hasBlackBadge,
    ).toDomain(),
    email = email,
    isEmailVerified = emailVerified,
    role = role.toDomain(),
    // The contract marks `created_at` required, so this parse should never fail. It goes through
    // the forgiving [parseTimestamp] anyway, for the reason that function exists: a date this
    // build cannot read must cost one line on the profile, not the whole screen.
    joinedAt = parseTimestamp(createdAt),
    version = version,
    usernameChangeAvailableAt = parseTimestamp(usernameChangeAvailableAt),
)

internal fun UserPublicDto.toUserProfile(): UserProfile = UserProfile(
    user = toDomain(),
    visibility = when (profileVisibility) {
        ProfileVisibilityDto.LIMITED -> ProfileVisibility.LIMITED
        // FULL, and anything a later contract adds. A value this build cannot read must not
        // hide a profile the server was willing to show in full.
        else -> ProfileVisibility.FULL
    },
    isBlockedByMe = isBlockedByMe,
    profileUrl = profileUrl,
    // Straight through, NOT `?: false`: null here means the viewer is not allowed to know,
    // which is a different thing from being offline.
    isOnline = isOnline,
    lastSeenAt = parseTimestamp(lastSeenAt),
)

private fun UserRoleDto.toDomain(): AccountRole = when (this) {
    UserRoleDto.USER -> AccountRole.USER
    UserRoleDto.MODERATOR -> AccountRole.MODERATOR
    UserRoleDto.ADMIN -> AccountRole.ADMIN
    UserRoleDto.UNKNOWN -> AccountRole.UNKNOWN
}

private fun QrCode.toDomain(): ProfileQr = ProfileQr(
    payload = payload,
    imageUrl = imageUrl,
    target = when (targetType) {
        QrTargetTypeDto.USER -> QrTarget.USER
        QrTargetTypeDto.PUBLIC_GROUP -> QrTarget.PUBLIC_GROUP
        QrTargetTypeDto.PRIVATE_GROUP_INVITE -> QrTarget.PRIVATE_GROUP_INVITE
        QrTargetTypeDto.UNKNOWN -> QrTarget.UNKNOWN
    },
    targetId = targetId,
)

private fun AccountDeletionStatus.toDomain(): ScheduledDeletion = ScheduledDeletion(
    scheduledFor = parseTimestamp(scheduledFor),
    recoveryDays = recoveryDays,
)

/**
 * Builds the `updateMe` body.
 *
 * Only mentioned fields appear. [FieldEdit.Clear] becomes a literal JSON null; a blank value is
 * treated as a clear, because "" and "no bio" are the same thing to the user and storing the
 * empty string would make every reader test for it.
 */
private fun ProfileEdit.toJsonBody(): JsonObject = buildJsonObject {
    put("version", JsonPrimitive(version))
    displayName?.let { put("display_name", JsonPrimitive(it.trim())) }
    // The contract's Username pattern is lowercase-only, so normalising here turns a 400 that
    // the user cannot act on into a request that does what they meant.
    username?.let { put("username", JsonPrimitive(it.trim().lowercase())) }

    patchString("bio", bio)
    patchString("date_of_birth", dateOfBirth)
    patchBoolean("show_birth_year", showBirthYear)
    patchString("location", location)
    if (useLocationHint) put("use_location_hint", JsonPrimitive(true))

    when (val edit = avatarMediaId) {
        is FieldEdit.Unchanged -> Unit
        is FieldEdit.Clear -> put("avatar_media_id", JsonNull)
        is FieldEdit.Set -> put("avatar_media_id", JsonPrimitive(edit.value))
    }
}

private fun JsonObjectBuilder.patchString(name: String, edit: FieldEdit<String>) {
    when (edit) {
        is FieldEdit.Unchanged -> Unit
        is FieldEdit.Clear -> put(name, JsonNull)
        is FieldEdit.Set -> {
            val text = edit.value.trim()
            put(name, if (text.isEmpty()) JsonNull else JsonPrimitive(text))
        }
    }
}

private fun JsonObjectBuilder.patchBoolean(name: String, edit: FieldEdit<Boolean>) {
    when (edit) {
        is FieldEdit.Unchanged -> Unit
        is FieldEdit.Clear -> put(name, JsonNull)
        is FieldEdit.Set -> put(name, JsonPrimitive(edit.value))
    }
}
